"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.WorkflowModel = exports.filterVariableGroupsByWhitelist = void 0;
/* eslint-disable max-params */
/* eslint-disable no-prototype-builtins */
const workflow_validation_1 = require("@whispir/workflow-validation");
const modelUtils_1 = require("./modelUtils");
const nodeUtils_1 = require("./nodeUtils");
const contentBuilderInputs = [
    'form-text-input',
    'form-text-area',
    'form-date-input',
    'form-slider',
    'form-yes-no-buttons',
    'form-radio-list',
    'form-checkbox-list',
    'form-dropdown',
];
// Just keeping this whitelist function alongside the WorkflowModel class for easier portability
// when we move this to Workflow Engine.
const recipientVariableWhitelist = [
    'FormTextInput',
    'FormTextArea',
    'FormRadioList',
    'FormCheckboxList',
    'FormDropdown',
    'FormYesNoButtons',
    'Recipient',
    ...contentBuilderInputs,
];
const messageContentVariableWhitelist = [
    'FormTextInput',
    'FormTextArea',
    'FormDatePicker',
    'FormRadioList',
    'FormCheckboxList',
    'FormDropdown',
    'FormNumericSlider',
    'FormYesNoButtons',
    'PhoneNumber',
    'Text',
    'Number',
    'Date',
    ...contentBuilderInputs,
];
const expressionVariableWhitelist = [
    'FormTextInput',
    'FormTextArea',
    'FormDatePicker',
    'FormRadioList',
    'FormCheckboxList',
    'FormDropdown',
    'FormNumericSlider',
    'FormYesNoButtons',
    'PhoneNumber',
    'Text',
    'Number',
    'Date',
    ...contentBuilderInputs,
];
const variableDestinationWhitelists = {
    recipient: recipientVariableWhitelist,
    messageContent: messageContentVariableWhitelist,
    expression: expressionVariableWhitelist,
    contentBuilder: [],
};
const filterVariableGroupsByWhitelist = ({ variableGroups, destination, }) => {
    const variableDestinationWhitelist = variableDestinationWhitelists[destination];
    if (!variableDestinationWhitelist) {
        throw new Error(`No variable whitelist defined for destination: '${destination}'`);
    }
    if (destination !== 'contentBuilder') {
        return variableGroups.map((variableGroup) => {
            const { variables } = variableGroup;
            const filteredVariables = variables.filter(({ type }) => variableDestinationWhitelist.includes(type));
            const filteredVariableGroup = Object.assign(Object.assign({}, variableGroup), { variables: filteredVariables });
            return filteredVariableGroup;
        });
    }
    return variableGroups;
};
exports.filterVariableGroupsByWhitelist = filterVariableGroupsByWhitelist;
class WorkflowModel {
    constructor() {
        /* eslint-disable lines-between-class-members */
        this.id = '';
        this.title = '';
        this.version = '0';
        this.structure = [];
        this.errors = {};
        this.started = false;
        this.description = '';
        this.addStep = (previousNode, actionType, workspaceId) => {
            this.structure = (0, modelUtils_1.addNewNode)(previousNode, actionType, this.structure, workspaceId);
        };
        this.addStepInBetween = (previousNode, actionType, index, isDirectlyFollowedByABranch, workspaceId) => {
            this.structure = (0, modelUtils_1.addNewNodeInBetween)(previousNode, actionType, this.structure, index, isDirectlyFollowedByABranch, workspaceId);
        };
        this.deleteStep = (nodeId, index) => {
            this.structure = (0, modelUtils_1.deleteSelectedNode)(nodeId, this.structure, index);
        };
        this.deleteStepWithDepth = (nodeId, index) => {
            this.structure = (0, modelUtils_1.deleteSelectedNodeWithDepth)(nodeId, this.structure, index);
        };
        this.deleteSplit = (nodeId) => {
            this.structure = (0, modelUtils_1.deleteAllChildren)(nodeId, this.structure);
        };
        this.deleteBranch = (nodeId, index) => {
            this.structure = (0, modelUtils_1.deleteSelectedBranch)(nodeId, this.structure, index);
        };
        this.addBranch = (previousNode) => {
            this.structure = (0, modelUtils_1.addNewBranch)(previousNode, this.structure);
        };
        this.doesNodeHaveChildren = (id) => {
            const node = (0, modelUtils_1.findNodeById)(this.structure[0], id);
            return (0, modelUtils_1.doesNodeHaveChildren)(node);
        };
        // Mutative method.
        this.replaceOutputVariables = (id, updatedVariables) => {
            const node = (0, modelUtils_1.findNodeById)(this.structure[0], id);
            // dont update when variables are empty or node isn't
            if (!node)
                return;
            node.variables.output = updatedVariables;
        };
        // variables are now generated from the output variables of content-builder API
        // Mutative method.
        this.updateNodeData = (id, data) => {
            const node = (0, modelUtils_1.findNodeById)(this.structure[0], id);
            if (!node)
                return;
            // flag node as having changed
            const currentNodeData = node.data;
            const updatedNodedata = Object.assign(Object.assign({}, currentNodeData), data);
            node.data = updatedNodedata;
            node.isDirty = true;
            // reset and re-run validation for this node after every update
            delete this.errors[id];
        };
        /**
         *
         * @param {*} id
         * @param {*} duration
         * @param {*} preserveLeftBranch if using this feature, when you disable collect responses (set duration to null), then the existing nodes on the left (on-sms-reply) branch will be shifted to the current nodes children.
         */
        this.setCollectResponsesForNode = (id, duration, preserveLeftBranch = false) => {
            const node = (0, modelUtils_1.findNodeById)(this.structure[0], id);
            (0, modelUtils_1.setCollectResponseCaptureForNode)(node, duration, preserveLeftBranch);
            // Perform a blank update on the send-message, on-reply, and on-no-reply steps
            // to simulate a change to make the nodes "dirty" and delete the validation errors
            // for each node.
            //
            // In the future, we probably want to refactor this function to extract logic from
            // `modelUtils.setCollectResponseCaptureForNode` into this function, to perform non-blank
            // updates with `updateNodeData` on all response capture nodes.
            this.updateNodeData(node.id, {});
            if (duration) {
                this.updateNodeData(node.next[0].id, {});
                this.updateNodeData(node.next[1].id, {});
            }
        };
        this.getWorkflowVariablesByStepId = (id) => this.recursiveGetWorkflowVariablesByStepId(this.structure[0], id, []);
        this.getWorkflowVariableGroupsByStepId = (id, destination) => {
            const variableGroups = (0, modelUtils_1.recursiveGetVariableGroupsByStepId)(this.structure[0], id, []);
            if (destination) {
                const whitelistedVariableGroups = (0, exports.filterVariableGroupsByWhitelist)({
                    variableGroups,
                    destination,
                });
                return whitelistedVariableGroups;
            }
            return variableGroups;
        };
        /**
         * Convenience function that returns available variables as a simple array.
         */
        this.getAvailableVariablesAsArray = (id) => {
            const theseVars = this.getWorkflowVariablesByStepId(id);
            if (!theseVars) {
                throw new Error(`Nothing returned from this.getWorkflowStepsByVariableId id = ${id}`, theseVars);
            }
            return [...theseVars];
        };
        this.getWorkflowVariableObjById = (variableId, stepId) => {
            const variables = this.getWorkflowVariablesByStepId(stepId);
            return variables.length
                ? variables.find((item) => item.variableId === variableId)
                : null;
        };
        this.getWorkflowVariableNameById = (variableId, stepId) => {
            const variableObj = this.getWorkflowVariableObjById(variableId, stepId);
            return variableObj ? variableObj.variableName : null;
        };
        this.getWorkflowStepsByVariableId = (id) => (0, modelUtils_1.findNodes)(this.structure[0], (node) => (0, nodeUtils_1.doesNodeContainsVariableId)(node, id));
    }
    /* eslint-enable lines-between-class-members */
    setWorkflowSchema(schema) {
        // update internals
        this.id = schema.id;
        this.title = schema.title;
        this.version = schema.version;
        // no idea if we are actually using description, leaving this in for now
        if (schema.description) {
            this.description = schema.description.slice();
        }
        this.structure = JSON.parse(JSON.stringify(schema.structure));
    }
    validate() {
        /**
         * nb.
         * This may look like a bit of inefficiency here, where we essentially validate the workflow twice.
         *
         * The reason we are doing this is because `validateWorkflowDefinition` is what we are doing on the backend.
         * `validateNodes` gives us a detailed view of what is wrong with the workflow.
         *
         * We don't want a situation where workflows are detected as valid on the FE but invalid on the BE  - hence we must call
         * validateWorkflowDefinition on the FE.
         */
        const result = (0, workflow_validation_1.validateWorkflowDefinition)(this.structure);
        if (result) {
            this.errors = (0, workflow_validation_1.validateNodes)((0, modelUtils_1.findNodes)(this.structure[0], () => true));
            // Just log the errors - good for developer experience
            Object.values(this.errors)
                .reduce((acc, cur) => [...acc, ...cur], [])
                .forEach((v) => {
                console.info(v);
            });
            return !this.hasErrors;
        }
        this.errors = {};
        return true;
    }
    get hasErrors() {
        return Object.keys(this.errors).length > 0;
    }
    hasErrorForNode(nodeId) {
        return this.errors.hasOwnProperty(nodeId);
    }
    /**
     *
     * This function returns a set of legacy variables for the legacy 'WorkflowVariableMenu'.
     * It will be deprecasted as part of IN-101
     */
    recursiveGetWorkflowVariablesByStepId(currentNode, nodeId, accumulatedVariables) {
        if (currentNode.id === nodeId) {
            return accumulatedVariables;
        }
        if (currentNode.next) {
            const newAccumulatedVariables = [
                ...accumulatedVariables,
                ...currentNode.variables.output.map((variable) => (Object.assign(Object.assign({}, variable), { stepId: currentNode.id }))),
            ];
            const variablesToReturn = [];
            currentNode.next.forEach((node) => {
                variablesToReturn.push(...this.recursiveGetWorkflowVariablesByStepId(node, nodeId, newAccumulatedVariables));
            });
            return variablesToReturn;
        }
        return [];
    }
}
exports.WorkflowModel = WorkflowModel;
//# sourceMappingURL=WorkflowModel.js.map